/**
 * Portal API client — four calls back the onboarding wizard.
 *
 * `getInvitationStatus` is unauthenticated (the SPA's pre-sign-in link
 * probe). `postSignIn` and `postConfirm` carry the customer's Microsoft
 * ID token and return a discriminated `result` the SPA maps straight to
 * a screen. `postConfirm` (Phase 3) is the one that writes — it binds
 * the Teams tenant and accepts the invitation. `getManifest` (Phase 4)
 * fetches the gated `finn.zip` Teams package as a Blob.
 *
 * Shapes mirror the pydantic models in engine/finn/portal/.
 */
import { env } from "@/lib/env";

export type InvitationStatus =
  | "pending"
  | "accepted"
  | "installed"
  | "expired"
  | "revoked"
  | "not_found";

export type SignInResultKind =
  | "ok"
  | "email_mismatch"
  | "expired"
  | "revoked"
  | "not_found"
  | "already_used";

export type SignInResult = {
  result: SignInResultKind;
  invited_email: string | null;
  signed_in_email: string | null;
  signed_in_name: string | null;
  tenant_id: string | null;
  customer_id: string | null;
  customer_display_name: string | null;
  verified_domain: string | null;
};

export type ConfirmResultKind =
  | "ok"
  | "tenant_conflict"
  | "email_mismatch"
  | "expired"
  | "revoked"
  | "not_found"
  | "already_used";

export type ConfirmResult = {
  result: ConfirmResultKind;
  customer_display_name: string | null;
};

async function asJson<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
  return (await resp.json()) as T;
}

export async function getInvitationStatus(
  token: string,
): Promise<InvitationStatus> {
  const resp = await fetch(
    `${env.apiBaseUrl}/invitation?token=${encodeURIComponent(token)}`,
  );
  const data = await asJson<{ status: InvitationStatus }>(resp);
  return data.status;
}

export async function postSignIn(
  inviteToken: string,
  idToken: string,
): Promise<SignInResult> {
  const resp = await fetch(`${env.apiBaseUrl}/invitation/sign-in`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ token: inviteToken }),
  });
  return asJson<SignInResult>(resp);
}

export async function postConfirm(
  inviteToken: string,
  idToken: string,
): Promise<ConfirmResult> {
  const resp = await fetch(`${env.apiBaseUrl}/invitation/confirm`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ token: inviteToken }),
  });
  return asJson<ConfirmResult>(resp);
}

/** Fetch the gated `finn.zip` Teams package — authenticated, returns the
 *  archive bytes as a Blob the caller saves to disk. */
export async function getManifest(idToken: string): Promise<Blob> {
  const resp = await fetch(`${env.apiBaseUrl}/manifest`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
  return resp.blob();
}

// `/me` — resolve the signed-in user's customer (Phase 1: happy path).
// `not_onboarded` is part of the contract from the start; the SPA
// renders it in Phase 2. Phase 3 (RAG knowledge) added the
// `is_onboarding_admin` + `consent_acknowledged_at` fields used by the
// `/knowledge` route guard and the first-upload consent modal.
export type MeResultKind = "ok" | "not_onboarded";

export type MeResult = {
  result: MeResultKind;
  customer_id: string | null;
  customer_display_name: string | null;
  install_verified_at: string | null;
  signed_in_email: string | null;
  signed_in_name: string | null;
  is_onboarding_admin: boolean;
  consent_acknowledged_at: string | null;
};

/** Fetch the resolved-customer record for the signed-in user. The Bearer
 *  token is optional so local dev with FINN_PORTAL_AUTH_DISABLED can hit
 *  the endpoint without MSAL being wired up. */
export async function getMe(idToken: string | null): Promise<MeResult> {
  const headers: Record<string, string> = {};
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  const resp = await fetch(`${env.apiBaseUrl}/me`, { headers });
  return asJson<MeResult>(resp);
}

// ----------------------------------------------------------- RAG knowledge

// Status vocabulary mirrors the CHECK constraint on
// agent_memory.customer_rag_documents.status.
export type RagDocStatus = "pending" | "embedding" | "ready" | "failed";

export type RagDocument = {
  doc_id: number;
  title: string;
  filename: string;
  content_type: string;
  status: RagDocStatus;
  page_count: number | null;
  chunk_count: number | null;
  error_text: string | null;
  created_at: string;
  updated_at: string;
};

export type RagDocumentList = {
  documents: RagDocument[];
  quota_used: number;
  quota_max: number;
};

export type RagDocumentCreated = {
  doc_id: number;
  status: RagDocStatus;
  quota_used: number;
  quota_max: number;
};

export type RagDocumentDeleted = {
  doc_id: number;
  deleted: boolean;
};

export type RagConsent = {
  acknowledged_at: string;
  already_acknowledged: boolean;
};

/** Detail shape returned by the engine on a rejected upload (415, 413,
 *  409). FastAPI wraps the locked pt-BR message in `{detail: ...}`. */
export type RagUploadError = { detail: string; status: number };

function authHeaders(idToken: string | null): Record<string, string> {
  return idToken ? { Authorization: `Bearer ${idToken}` } : {};
}

export async function listRagDocuments(
  idToken: string | null,
): Promise<RagDocumentList> {
  const resp = await fetch(`${env.apiBaseUrl}/rag/documents`, {
    headers: authHeaders(idToken),
  });
  return asJson<RagDocumentList>(resp);
}

/** Upload a single file. Throws RagUploadError on a 4xx — the SPA shows
 *  the engine's pt-BR `detail` verbatim. Network/5xx fall back to the
 *  generic Error path. */
export async function uploadRagDocument(
  idToken: string | null,
  file: File,
): Promise<RagDocumentCreated> {
  const body = new FormData();
  body.append("file", file, file.name);
  const resp = await fetch(`${env.apiBaseUrl}/rag/documents`, {
    method: "POST",
    headers: authHeaders(idToken),
    body,
  });
  if (!resp.ok) {
    // The 4xx contract: the engine returns `{detail: <pt-BR>}` for the
    // 415/413/409 paths. Surface those verbatim; otherwise generic.
    if (resp.status >= 400 && resp.status < 500) {
      let detail = `HTTP ${resp.status}`;
      try {
        const body = await resp.json();
        if (body && typeof body.detail === "string") detail = body.detail;
      } catch {
        // fall through with the generic detail
      }
      const err: RagUploadError = { detail, status: resp.status };
      throw err;
    }
    throw new Error(`HTTP ${resp.status}`);
  }
  return (await resp.json()) as RagDocumentCreated;
}

export async function deleteRagDocument(
  idToken: string | null,
  docId: number,
): Promise<RagDocumentDeleted> {
  const resp = await fetch(`${env.apiBaseUrl}/rag/documents/${docId}`, {
    method: "DELETE",
    headers: authHeaders(idToken),
  });
  return asJson<RagDocumentDeleted>(resp);
}

export async function postRagConsent(
  idToken: string | null,
): Promise<RagConsent> {
  const resp = await fetch(`${env.apiBaseUrl}/rag/consent`, {
    method: "POST",
    headers: authHeaders(idToken),
  });
  return asJson<RagConsent>(resp);
}

// ----------------------------------------------------------- MCP servers
//
// Phase 7 — customer-portal day-2 surface. Shapes mirror
// engine/finn/portal/mcp_servers.py.

export type MCPServerMetrics24h = {
  call_count: number;
  error_count: number;
  error_rate: number;
  latency_ms_p50: number | null;
  latency_ms_p95: number | null;
};

export type MCPServerStatus =
  | "pending_iam"
  | "pending_probe"
  | "pending_approval"
  | "ready"
  | "disabled"
  | "auth_failed"
  | "degraded";

export type MCPServerListItem = {
  mcp_server_id: string;
  alias: string;
  display_name: string;
  description: string;
  status: MCPServerStatus;
  is_active: boolean;
  metrics_24h: MCPServerMetrics24h;
};

export type MCPTool = {
  tool_id: string;
  tool_name: string;
  namespaced_name: string;
  description: string;
  input_schema: string;
  output_schema: string;
  is_enabled: boolean;
  pending_tier: string | null;
  call_count_7d: number;
  error_count_7d: number;
};

export type MCPServerDetail = {
  mcp_server_id: string;
  alias: string;
  display_name: string;
  description: string;
  status: MCPServerStatus;
  is_active: boolean;
  base_url: string;
  timeout_seconds: number;
  metrics_24h: MCPServerMetrics24h;
  tools: MCPTool[];
};

export type MCPActionResult = {
  server: MCPServerListItem;
};

export type MCPReprobeResult = {
  tier_a_count: number;
  tier_b_count: number;
  tier_c_count: number;
};

export async function listMCPServers(
  idToken: string | null,
): Promise<MCPServerListItem[]> {
  const resp = await fetch(`${env.apiBaseUrl}/mcp-servers`, {
    headers: authHeaders(idToken),
  });
  return asJson<MCPServerListItem[]>(resp);
}

export async function getMCPServerDetail(
  idToken: string | null,
  serverId: string,
): Promise<MCPServerDetail> {
  const resp = await fetch(
    `${env.apiBaseUrl}/mcp-servers/${encodeURIComponent(serverId)}`,
    { headers: authHeaders(idToken) },
  );
  return asJson<MCPServerDetail>(resp);
}

export async function disableMCPServer(
  idToken: string | null,
  serverId: string,
): Promise<MCPActionResult> {
  const resp = await fetch(
    `${env.apiBaseUrl}/mcp-servers/${encodeURIComponent(serverId)}/disable`,
    { method: "POST", headers: authHeaders(idToken) },
  );
  return asJson<MCPActionResult>(resp);
}

export async function deleteMCPServer(
  idToken: string | null,
  serverId: string,
): Promise<void> {
  const resp = await fetch(
    `${env.apiBaseUrl}/mcp-servers/${encodeURIComponent(serverId)}`,
    { method: "DELETE", headers: authHeaders(idToken) },
  );
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
}

export async function rotateMCPToken(
  idToken: string | null,
  serverId: string,
  bearerToken: string,
): Promise<MCPActionResult> {
  const resp = await fetch(
    `${env.apiBaseUrl}/mcp-servers/${encodeURIComponent(serverId)}/rotate-token`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(idToken),
      },
      body: JSON.stringify({ bearer_token: bearerToken }),
    },
  );
  return asJson<MCPActionResult>(resp);
}

export async function reprobeMCPServer(
  idToken: string | null,
  serverId: string,
  urgent: boolean,
): Promise<MCPReprobeResult> {
  const resp = await fetch(
    `${env.apiBaseUrl}/mcp-servers/${encodeURIComponent(serverId)}/reprobe`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(idToken),
      },
      body: JSON.stringify({ urgent }),
    },
  );
  return asJson<MCPReprobeResult>(resp);
}
